import { DOCUMENT, inject, Injectable } from '@angular/core';
import { signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { palette, updatePrimaryPalette } from '@primeuix/themes';
import { PRIMARY_COLOR } from '../../config/theme.config';

export interface ThemeBackground {
  main: string;
  secondary: string;
  accent: string;
  overlay: string;
  primary: string;
  theme: string;
}

export interface StateType {
  direction: string;
  theme: string;
  navigationStyles: string;
  menuStyles: string;
  layoutStyles: string;
  pageStyles: string;
  widthStyles: string;
  menuPosition: string;
  headerPosition: string;
  menuColor: string;
  headerColor: string;
  themePrimary: string;
  // Object shape when set (see ThemeBackground); '' when unset. Kept loose because the
  // switcher template does dynamic bracket-access on this field.
  themeBackground: any;
  backgroundImage: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private readonly localStorageKey = 'Ynex-ng';
  private readonly initialState: StateType = {
    theme: 'light',
    direction: 'ltr',
    navigationStyles: 'vertical',
    menuStyles: '',
    layoutStyles: 'default',
    pageStyles: 'regular',
    widthStyles: 'fullwidth',
    menuPosition: 'fixed',
    headerPosition: 'fixed',
    menuColor: 'dark',
    headerColor: 'light',
    themePrimary: '',
    themeBackground: '',
    backgroundImage: '',
  };

  private readonly stateSignal = signal<StateType>(this.getInitialStateFromLocalStorage());
  readonly state = this.stateSignal.asReadonly();
  readonly state$ = toObservable(this.stateSignal);

  private document = inject(DOCUMENT);
  private html = this.document.documentElement;

  constructor() {
    this.updateStateAndEmit(this.stateSignal());
  }

  private getInitialStateFromLocalStorage(): StateType {
    try {
      const storedState = localStorage.getItem(this.localStorageKey);
      if (storedState) {
        return JSON.parse(storedState);
      }
    } catch (error) {
      console.error('Error retrieving initial state from local storage:', error);
    }
    return this.initialState;
  }

  getupdateState(): StateType {
    return this.stateSignal();
  }

  updateState(newState?: Partial<StateType>) {
    const currentState = this.stateSignal();

    if (!currentState) {
      if (newState) {
        this.updateStateAndEmit({ ...this.initialState, ...newState });
      }
      return;
    }
    if (newState) {
      const updatedState = { ...currentState, ...newState };
      this.updateStateAndEmit(updatedState);
    } else {
      this.updateStateAndEmit(currentState);
    }
  }

  private applyThemeBackgroundSpecificChanges(background: ThemeBackground) {
    this.html?.style.setProperty('--color-bodybg', background.main);
    this.html?.style.setProperty('--color-bodybg2', background.secondary);
    this.html?.style.setProperty('--color-light', background.accent);
    this.html?.style.setProperty('--color-formcontrolbg', `rgba(${background.accent})`);
    this.html?.style.setProperty('--color-inputborder', background.overlay);
    this.html?.style.setProperty('--color-gray3', background.primary);
    this.applythemeSpecificChanges(background.theme);
  }

  private applyDirectionSpecificChanges(direction: string) {
    this.html?.setAttribute('dir', direction);
  }

  private applythemeSpecificChanges(theme: string) {
    this.html?.setAttribute('class', theme);
    this.html?.setAttribute('data-header-styles', theme);
  }

  private applyNavigationStylesSpecificChanges(navigationStyles: string) {
    this.html?.setAttribute('data-nav-layout', navigationStyles);
    if (navigationStyles === 'horizontal') {
      this.html?.setAttribute('data-nav-style', 'menu-click');
      this.html?.removeAttribute('data-vertical-style');
    }
  }

  private applyMenuStylesSpecificChanges(menuStyles: string) {
    this.html?.setAttribute('data-nav-style', menuStyles);
    this.html?.setAttribute('data-toggled', menuStyles + '-closed');
    this.html?.removeAttribute('data-vertical-style');
  }

  private applyLayoutStylesSpecificChanges(layoutStyles: string) {
    this.html?.setAttribute('data-vertical-style', layoutStyles);
    this.html?.removeAttribute('data-nav-style');
    switch (layoutStyles) {
      case 'default':
        this.html?.setAttribute('data-vertical-style', 'overlay');
        this.html?.setAttribute('data-toggled', '');
        break;
      case 'closed':
        this.html?.setAttribute('data-toggled', 'close-menu-close');
        break;
      case 'icontext':
        this.html?.setAttribute('data-toggled', 'icon-text-close');
        break;
      case 'overlay':
        this.html?.setAttribute('data-toggled', 'icon-overlay-close');
        break;
      case 'detached':
        this.html?.setAttribute('data-toggled', 'detached-close');
        break;
      case 'doublemenu':
        this.html?.setAttribute('data-toggled', 'double-menu-open');
        break;
    }
    if (layoutStyles === 'icon-text') {
      this.html?.setAttribute('icon-text', 'open');
    } else {
      this.html?.removeAttribute('icon-text');
    }
  }

  private applypageStylesSpecificChanges(pageStyles: string) {
    this.html?.setAttribute('data-page-style', pageStyles);

    const slideRight = document.querySelector('.slide-right') as HTMLElement | null;
    if (slideRight) {
      if (slideRight.classList.contains('d-none')) {
        slideRight.classList.remove('d-none');
      } else {
        slideRight.classList.add('d-none');
      }
    } else {
      const dummySlideRight = document.createElement('div');
      dummySlideRight.classList.add('slide-right', 'd-none');
      document.body.appendChild(dummySlideRight);
    }
  }

  private applywidthStylesSpecificChanges(widthStyles: string) {
    this.html?.setAttribute('data-width', widthStyles);
  }

  private applymenuPositionSpecificChanges(menuPosition: string) {
    this.html?.setAttribute('data-menu-position', menuPosition);
  }

  private applyheaderPositionSpecificChanges(headerPosition: string) {
    this.html?.setAttribute('data-header-position', headerPosition);
  }

  private applyheaderColorSpecificChanges(headerColor: string) {
    this.html?.setAttribute('data-header-styles', headerColor);
  }

  private applymenuColorSpecificChanges(menuColor: string) {
    this.html?.setAttribute('data-menu-styles', menuColor);
  }

  private applyPrimarySpecificChanges(primary: string) {
    this.html?.style.setProperty('--color-primaryrgb', primary);
    this.html?.style.setProperty('--color-primary', primary);
    this.applyPrimeNgPrimary(primary);
  }

  /** The PrimeNG theme's primary palette (buttons, focus rings, the tints built from --primary-color). */
  private applyPrimeNgPrimary(primary: string) {
    try {
      updatePrimaryPalette(palette(primary) as Parameters<typeof updatePrimaryPalette>[0]);
    } catch {
      // PrimeNG's theme is not loaded yet; reapplyPrimary() runs again once the page is up.
    }
  }

  /** Applies the saved primary colour to PrimeNG (it can only be done once the theme has loaded). */
  public reapplyPrimary() {
    const saved = this.stateSignal().themePrimary;
    if (saved) {
      this.applyPrimeNgPrimary(saved);
    }
  }

  private applybackgroundImageSpecificChanges(backgroundImage: string) {
    this.html?.setAttribute('bg-img', backgroundImage);
  }

  public applyReset() {
    if (this.html) {
      this.html?.style.removeProperty('--color-bodybg');
      this.html?.style.removeProperty('--color-gray3');
      this.html?.style.removeProperty('--color-bodybg2');
      this.html?.style.removeProperty('--color-light');
      this.html?.style.removeProperty('--color-formcontrolbg');
      this.html?.style.removeProperty('--color-inputborder');
      this.html?.style.removeProperty('--color-primary');
      this.html?.style.removeProperty('--color-primaryrgb');
    }
    this.html?.removeAttribute('bg-img');
    this.html?.setAttribute('data-vertical-style', 'overlay');
    this.applyPrimeNgPrimary(PRIMARY_COLOR);
    this.updateStateAndEmit(this.initialState);
    try {
      localStorage.removeItem(this.localStorageKey);
    } catch (error) {
      console.error('Error resetting local storage:', error);
    }

    if (window.innerWidth <= 992) {
      this.html?.setAttribute('data-toggled', 'close');
    }
  }

  private updateStateAndEmit(state: StateType) {
    if (state.theme) {
      this.applythemeSpecificChanges(state.theme);
    }
    if (state.direction) {
      this.applyDirectionSpecificChanges(state.direction);
    }
    if (state.navigationStyles) {
      this.applyNavigationStylesSpecificChanges(state.navigationStyles);
    }
    if (state.menuStyles && !state.layoutStyles) {
      this.applyMenuStylesSpecificChanges(state.menuStyles);
    }
    if (state.layoutStyles && !state.menuStyles) {
      this.applyLayoutStylesSpecificChanges(state.layoutStyles);
    }
    if (state.pageStyles) {
      this.applypageStylesSpecificChanges(state.pageStyles);
    }
    if (state.widthStyles) {
      this.applywidthStylesSpecificChanges(state.widthStyles);
    }
    if (state.menuPosition) {
      this.applymenuPositionSpecificChanges(state.menuPosition);
    }
    if (state.headerPosition) {
      this.applyheaderPositionSpecificChanges(state.headerPosition);
    }
    if (state.themePrimary) {
      this.applyPrimarySpecificChanges(state.themePrimary);
    }
    if (state.themeBackground && typeof state.themeBackground === 'object') {
      this.applyThemeBackgroundSpecificChanges(state.themeBackground);
    }
    if (state.headerColor) {
      this.applyheaderColorSpecificChanges(state.headerColor);
    }
    if (state.menuColor) {
      this.applymenuColorSpecificChanges(state.menuColor);
    }
    if (state.backgroundImage) {
      this.applybackgroundImageSpecificChanges(state.backgroundImage);
    }

    this.stateSignal.set(state);
    this.updateLocalStorage(state);
  }

  private updateLocalStorage(state: StateType) {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state to local storage:', error);
    }
  }
}
